import React, { useState } from "react";
import { 
  FiCheckCircle, 
  FiXCircle, 
  FiHelpCircle, 
  FiArrowRight, 
  FiShield, 
  FiAward,
  FiFileText
} from "react-icons/fi";
import type { 
  IsIsNotAnalysis, 
  IsIsNotDimension, 
  IsIsNotHypothesis,
  RcaMethodCode 
} from "../../../../types/rca.types.js";

export type IsIsNotWorkspaceProps = {
  rcaRecordId?: string;
  problemStatement: string;
  readOnly?: boolean;
  onSave?: (analysis: IsIsNotAnalysis) => void;
  onChainMethod?: (method: RcaMethodCode, hypothesis: string) => void;
  onRootCauseIdentified?: (rootCauseText: string, category: string, confidence: number) => void;
};

const INITIAL_DIMENSIONS: IsIsNotDimension[] = [
  { dimension: "WHAT", isFact: "Scratch & dimension offset on part #PN-12345", isNotFact: "Crack or material porosity defect", distinction: "Surface finish & length affected only", evidenceText: "CMM Report #081" },
  { dimension: "WHERE", isFact: "Line 2 CNC Milling Station", isNotFact: "Line 1 or Line 3 CNC Stations", distinction: "Station 2 tool holder #3 changed recently", evidenceText: "Line Shift Log" },
  { dimension: "WHEN", isFact: "Aug 20+ (Night Shift)", isNotFact: "Before Aug 20 (Day Shift)", distinction: "New coolant batch mixed on Aug 20", evidenceText: "Chemical Mix Log" },
  { dimension: "EXTENT", isFact: "125 pcs affected (Batch LOT-2026-0789)", isNotFact: "All parts from raw stock heat #99", distinction: "Confined to parts machined on spindle #2", evidenceText: "QA Quarantine Sheet" },
];

const INITIAL_HYPOTHESES: IsIsNotHypothesis[] = [
  { id: "h-1", hypothesis: "Calibration Drift on CNC Station 2 Tool Holder", consistentWithWhat: true, consistentWithWhere: true, consistentWithWhen: true, consistentWithExtent: true, isSurviving: true, testNotes: "Passed all 4 dimension tests. Strongly supported by maintenance logs." },
  { id: "h-2", hypothesis: "Raw Material Alloy Hardness Fluctuation", consistentWithWhat: true, consistentWithWhere: false, consistentWithWhen: false, consistentWithExtent: false, isSurviving: false, testNotes: "Contradicts WHERE and WHEN (other lines had no issues with same raw material)." },
  { id: "h-3", hypothesis: "Operator Inexperience", consistentWithWhat: false, consistentWithWhere: true, consistentWithWhen: false, consistentWithExtent: false, isSurviving: false, testNotes: "Contradicts WHAT (automated CNC program, not manual operation)." },
];

export function IsIsNotWorkspace({
  rcaRecordId = "rca-current",
  problemStatement,
  readOnly = false,
  onSave,
  onChainMethod,
  onRootCauseIdentified,
}: IsIsNotWorkspaceProps) {
  const [activeTab, setActiveTab] = useState<"matrix" | "hypotheses">("matrix");
  const [dimensions, setDimensions] = useState<IsIsNotDimension[]>(INITIAL_DIMENSIONS);
  const [hypotheses, setHypotheses] = useState<IsIsNotHypothesis[]>(INITIAL_HYPOTHESES);

  const survivingHypothesis = hypotheses.find(h => h.isSurviving);

  const handleConfirmHypothesis = () => {
    if (survivingHypothesis) {
      onRootCauseIdentified?.(survivingHypothesis.hypothesis, "MACHINE", 88);
      onChainMethod?.("FIVE_WHY", survivingHypothesis.hypothesis);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-purple-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
            IS
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              16. Is / Is Not Analysis Workspace
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                Boundary & Contrast Analysis
              </span>
            </h2>
            <p className="text-xs text-slate-500">
              Sharpen problem boundaries by asking what the problem IS versus what it IS NOT to filter plausible hypotheses.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 p-1 rounded-lg text-xs font-medium border border-slate-200">
            <button
              type="button"
              onClick={() => setActiveTab("matrix")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "matrix" 
                  ? "bg-white text-purple-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              IS / IS NOT Matrix
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("hypotheses")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "hypotheses" 
                  ? "bg-white text-purple-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Hypotheses Testing ({hypotheses.length})
            </button>
          </div>
        </div>
      </div>

      {/* Surviving Hypothesis Strip */}
      <div className="px-6 py-2.5 bg-purple-50/50 border-b border-purple-100 flex flex-wrap items-center justify-between gap-4 text-xs">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-purple-900">Surviving Hypothesis:</span>
          {survivingHypothesis ? (
            <span className="px-3 py-0.5 rounded-full bg-purple-600 text-white font-bold text-xs shadow-sm flex items-center gap-1">
              <FiAward className="w-3.5 h-3.5" />
              {survivingHypothesis.hypothesis}
            </span>
          ) : (
            <span className="text-slate-500">None surviving all dimensions</span>
          )}
        </div>

        {survivingHypothesis && (
          <button
            type="button"
            onClick={handleConfirmHypothesis}
            className="px-3.5 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold text-xs flex items-center gap-1.5 shadow-sm transition-colors"
          >
            Next Method (Chain 5-Why) <FiArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === "matrix" ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border border-slate-200 rounded-lg">
              <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3 w-28">Dimension</th>
                  <th className="py-2.5 px-3 bg-emerald-50 text-emerald-900 font-bold border-l border-r border-slate-200">IS (Specific Fact)</th>
                  <th className="py-2.5 px-3 bg-rose-50 text-rose-900 font-bold border-r border-slate-200">IS NOT (Could be but is not)</th>
                  <th className="py-2.5 px-3">Distinction / Change</th>
                  <th className="py-2.5 px-3">Evidence Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dimensions.map(d => (
                  <tr key={d.dimension} className="hover:bg-slate-50">
                    <td className="py-3 px-3 font-bold text-purple-700 bg-slate-50">
                      {d.dimension}
                    </td>
                    <td className="py-3 px-3 text-slate-900 font-medium bg-emerald-50/30 border-l border-r border-slate-100">
                      {d.isFact}
                    </td>
                    <td className="py-3 px-3 text-slate-600 bg-rose-50/30 border-r border-slate-100">
                      {d.isNotFact}
                    </td>
                    <td className="py-3 px-3 text-slate-800 font-medium">
                      {d.distinction}
                    </td>
                    <td className="py-3 px-3 text-slate-500 font-mono text-[11px]">
                      {d.evidenceText || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* Hypotheses Testing Matrix */
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border border-slate-200 rounded-lg">
                <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="py-2.5 px-3">Hypothesis</th>
                    <th className="py-2.5 px-3 text-center">Consistent WHAT?</th>
                    <th className="py-2.5 px-3 text-center">Consistent WHERE?</th>
                    <th className="py-2.5 px-3 text-center">Consistent WHEN?</th>
                    <th className="py-2.5 px-3 text-center">Consistent EXTENT?</th>
                    <th className="py-2.5 px-3">Surviving Status</th>
                    <th className="py-2.5 px-3">Test Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {hypotheses.map(h => (
                    <tr key={h.id} className={h.isSurviving ? "bg-purple-50/40 font-medium" : ""}>
                      <td className="py-3 px-3 font-bold text-slate-900">{h.hypothesis}</td>
                      <td className="py-3 px-3 text-center">
                        {h.consistentWithWhat ? <FiCheckCircle className="w-4 h-4 text-emerald-600 inline" /> : <FiXCircle className="w-4 h-4 text-rose-600 inline" />}
                      </td>
                      <td className="py-3 px-3 text-center">
                        {h.consistentWithWhere ? <FiCheckCircle className="w-4 h-4 text-emerald-600 inline" /> : <FiXCircle className="w-4 h-4 text-rose-600 inline" />}
                      </td>
                      <td className="py-3 px-3 text-center">
                        {h.consistentWithWhen ? <FiCheckCircle className="w-4 h-4 text-emerald-600 inline" /> : <FiXCircle className="w-4 h-4 text-rose-600 inline" />}
                      </td>
                      <td className="py-3 px-3 text-center">
                        {h.consistentWithExtent ? <FiCheckCircle className="w-4 h-4 text-emerald-600 inline" /> : <FiXCircle className="w-4 h-4 text-rose-600 inline" />}
                      </td>
                      <td className="py-3 px-3">
                        {h.isSurviving ? (
                          <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-purple-100 text-purple-800">
                            SURVIVING
                          </span>
                        ) : (
                          <span className="text-slate-400">Rejected</span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-slate-500 text-[11px]">{h.testNotes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
